import { Component, Inject, Optional, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule } from '@ngx-translate/core';
import { fromCustomImageActions, fromCustomImageSelectors } from '@design/store/custom-image';
import { DesignState } from '@design/store/design.reducer';

export interface CustomImageCalibrationDialogData {
  pixelDistance: number;
  point1: { x: number; y: number; z: number } | null;
  point2: { x: number; y: number; z: number } | null;
  measurementUnit: 'ft' | 'm';
  pointsCount: number;
}

export interface CustomImageCalibrationDialogResult {
  confirmed: boolean;
  actualDistance: number;
  unit: 'ft' | 'm';
}

@Component({
  selector: 'app-custom-image-calibration-dialog',
  templateUrl: './custom-image-calibration-dialog.component.html',
  styleUrls: ['./custom-image-calibration-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, TranslateModule]
})
export class CustomImageCalibrationDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('distanceInput') distanceInput!: ElementRef<HTMLInputElement>;
  
  pixelDistance: number = 0;
  actualDistance: number | null = null;
  displayValue: string = '--';
  selectedUnit: 'ft' | 'm' = 'ft';
  pointsCount: number = 0;
  private isFocused: boolean = false;
  private subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<CustomImageCalibrationDialogComponent, CustomImageCalibrationDialogResult>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CustomImageCalibrationDialogData,
    private store: Store<DesignState>,
    private actions$: Actions
  ) {
    if (data) {
      this.pixelDistance = data.pixelDistance || 0;
      // Get measurement unit from design preferences
      this.selectedUnit = data.measurementUnit || 'ft';
      this.pointsCount = data.pointsCount || 0;
    }
    // Start with placeholder displayed
    this.actualDistance = null;
    this.displayValue = '--';
  }

  ngOnInit(): void {
    // Subscribe to calibration points count updates
    this.subscription.add(
      this.store.select(fromCustomImageSelectors.selectCalibrationPointsCount)
        .subscribe((count) => {
          this.pointsCount = count;
          // Auto-focus input when second point is placed
          if (count === 2 && this.distanceInput) {
            setTimeout(() => {
              this.distanceInput.nativeElement.focus();
            }, 100);
          }
        })
    );

    // Listen for focus action
    this.subscription.add(
      this.actions$.pipe(
        ofType(fromCustomImageActions.FocusCalibrationDialogInput)
      ).subscribe(() => {
        if (this.distanceInput) {
          setTimeout(() => {
            this.distanceInput.nativeElement.focus();
          }, 100);
        }
      })
    );
  }

  ngAfterViewInit(): void {
    // If we already have 2 points when dialog opens, focus the input
    if (this.pointsCount === 2 && this.distanceInput) {
      setTimeout(() => {
        this.distanceInput.nativeElement.focus();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Clears the input when focused so user can type fresh
   */
  onInputFocus(event: FocusEvent): void {
    this.isFocused = true;
    if (this.actualDistance === null || this.displayValue === '--') {
      this.displayValue = '';
      this.actualDistance = null;
    }
  }

  /**
   * Restores placeholder if user leaves input empty, or formats to 2 decimal places
   */
  onInputBlur(event: FocusEvent): void {
    this.isFocused = false;
    if (this.displayValue === '' || this.actualDistance === null || this.actualDistance === undefined) {
      this.actualDistance = null;
      this.displayValue = '--';
    } else if (this.actualDistance > 0) {
      // Round and format to 2 decimal places on blur
      this.actualDistance = Math.round(this.actualDistance * 100) / 100;
      this.displayValue = this.actualDistance.toFixed(2);
    }
  }

  /**
   * Handle input changes - round to 2 decimal places
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.displayValue = value;
    if (value === '' || value === null) {
      this.actualDistance = null;
    } else {
      const parsed = parseFloat(value);
      this.actualDistance = isNaN(parsed) ? null : Math.round(parsed * 100) / 100;
    }
  }

  isValid(): boolean {
    return this.actualDistance !== null && this.actualDistance > 0;
  }

  onCancel(): void {
    console.log('[CalibrationDialog] Cancel clicked');
    this.dialogRef.close({
      confirmed: false,
      actualDistance: 0,
      unit: this.selectedUnit
    });
  }

  onConfirm(): void {
    if (!this.isValid()) return;
    
    console.log('[CalibrationDialog] Confirm clicked, distance:', this.actualDistance, this.selectedUnit);
    
    // Convert to feet if meters were selected (internal units are feet)
    let distanceInFeet = this.actualDistance!;
    if (this.selectedUnit === 'm') {
      distanceInFeet = this.actualDistance! * 3.28084; // meters to feet
    }
    
    this.dialogRef.close({
      confirmed: true,
      actualDistance: distanceInFeet,
      unit: this.selectedUnit
    });
  }
}
